import { useState, useEffect } from 'react';
import {
  Settings,
  Building2,
  Package,
  Bell,
  Ticket,
  Upload,
  Save,
  Loader2,
  Globe,
  Calendar,
  Clock,
  RotateCcw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useSettingsStore } from '@/stores/settingsStore';

type TabId = 'general' | 'assets' | 'notifications' | 'tickets';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ElementType;
}

const tabs: Tab[] = [
  { id: 'general', label: 'General', icon: Building2 },
  { id: 'assets', label: 'Asset Settings', icon: Package },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'tickets', label: 'Ticket Settings', icon: Ticket },
];

interface GeneralSettings {
  systemName: string;
  logoUrl: string;
  dateFormat: string;
  timeFormat: string;
  timezone: string;
  currency: string;
}

interface AssetSettings {
  autoGenerateAssetTags: boolean;
  assetTagPrefix: string;
  assetTagLength: number;
  requireSerialNumber: boolean;
  enableDepreciationTracking: boolean;
  defaultDepreciationMethod: string;
}

interface NotificationSettings {
  enableEmailNotifications: boolean;
  itNotificationEmail: string;
  warrantyExpiryAlertDays: number;
  maintenanceReminders: boolean;
  ticketUpdateNotifications: boolean;
  lowStockAlerts: boolean;
  documentExpiryAlerts: boolean;
}

interface TicketSettings {
  autoAssignTickets: boolean;
  defaultPriority: string;
  requireCategory: boolean;
  allowPublicTicketSubmission: boolean;
  slaBreachNotifications: boolean;
}

interface AllSettings {
  general: GeneralSettings;
  assets: AssetSettings;
  notifications: NotificationSettings;
  tickets: TicketSettings;
}

const defaultGeneralSettings: GeneralSettings = {
  systemName: 'Glide',
  logoUrl: '',
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24h',
  timezone: 'Africa/Nairobi',
  currency: 'UGX',
};

const defaultAssetSettings: AssetSettings = {
  autoGenerateAssetTags: true,
  assetTagPrefix: 'AST',
  assetTagLength: 6,
  requireSerialNumber: false,
  enableDepreciationTracking: false,
  defaultDepreciationMethod: 'straight-line',
};

const defaultNotificationSettings: NotificationSettings = {
  enableEmailNotifications: true,
  itNotificationEmail: 'it@ug.sanlamallianz.com',
  warrantyExpiryAlertDays: 30,
  maintenanceReminders: true,
  ticketUpdateNotifications: true,
  lowStockAlerts: true,
  documentExpiryAlerts: true,
};

const defaultTicketSettings: TicketSettings = {
  autoAssignTickets: false,
  defaultPriority: 'MEDIUM',
  requireCategory: true,
  allowPublicTicketSubmission: true,
  slaBreachNotifications: true,
};

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { updateSettings } = useSettingsStore();

  // Settings state
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>(defaultGeneralSettings);
  const [assetSettings, setAssetSettings] = useState<AssetSettings>(defaultAssetSettings);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(defaultNotificationSettings);
  const [ticketSettings, setTicketSettings] = useState<TicketSettings>(defaultTicketSettings);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings');
      const data = response.data?.data || response.data;
      if (data) {
        if (data.general) setGeneralSettings({ ...defaultGeneralSettings, ...data.general });
        if (data.assets) setAssetSettings({ ...defaultAssetSettings, ...data.assets });
        if (data.notifications) setNotificationSettings({ ...defaultNotificationSettings, ...data.notifications });
        if (data.tickets) setTicketSettings({ ...defaultTicketSettings, ...data.tickets });
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data: AllSettings = {
        general: generalSettings,
        assets: assetSettings,
        notifications: notificationSettings,
        tickets: ticketSettings,
      };
      await api.put('/settings', data);
      // Update the global settings store so currency/date formats apply immediately
      updateSettings(data);
      toast({
        title: 'Settings saved',
        description: 'Your settings have been saved successfully.',
        variant: 'success',
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    try {
      await api.post('/settings/reset');
      setGeneralSettings(defaultGeneralSettings);
      setAssetSettings(defaultAssetSettings);
      setNotificationSettings(defaultNotificationSettings);
      setTicketSettings(defaultTicketSettings);
      // Update the global settings store
      updateSettings({
        general: defaultGeneralSettings,
        assets: defaultAssetSettings,
        notifications: defaultNotificationSettings,
        tickets: defaultTicketSettings,
      });
      toast({
        title: 'Reset complete',
        description: 'Settings have been reset to defaults.',
        variant: 'success',
      });
    } catch (error) {
      console.error('Failed to reset settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to reset settings. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/svg+xml', 'image/jpeg', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PNG, SVG, JPEG, GIF, or WebP image.',
          variant: 'destructive',
        });
        e.target.value = '';
        return;
      }
      
      // Validate file size (max 500KB)
      const maxSize = 500 * 1024;
      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: 'Logo must be less than 500KB.',
          variant: 'destructive',
        });
        e.target.value = '';
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setGeneralSettings({ ...generalSettings, logoUrl: reader.result as string });
      };
      reader.onerror = () => {
        toast({
          title: 'Error',
          description: 'Failed to read file. Please try again.',
          variant: 'destructive',
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const timezones = [
    'Africa/Nairobi',
    'Africa/Lagos',
    'Africa/Johannesburg',
    'Africa/Cairo',
    'Africa/Kampala',
    'Africa/Dar_es_Salaam',
    'Africa/Kigali',
    'UTC',
    'Europe/London',
    'Europe/Paris',
    'America/New_York',
    'America/Los_Angeles',
    'Asia/Dubai',
    'Asia/Singapore',
  ];

  const dateFormats = [
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
    { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY' },
  ];

  const timeFormats = [
    { value: '12h', label: '12 Hour' },
    { value: '24h', label: '24 Hour' },
  ];

  const currencies = [
    { value: 'UGX', label: 'UGX - Ugandan Shilling' },
    { value: 'KES', label: 'KES - Kenyan Shilling' },
    { value: 'TZS', label: 'TZS - Tanzanian Shilling' },
    { value: 'RWF', label: 'RWF - Rwandan Franc' },
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'NGN', label: 'NGN - Nigerian Naira' },
    { value: 'ZAR', label: 'ZAR - South African Rand' },
    { value: 'EGP', label: 'EGP - Egyptian Pound' },
  ];

  const depreciationMethods = [
    { value: 'straight-line', label: 'Straight-line' },
    { value: 'declining-balance', label: 'Declining Balance' },
  ];

  const priorities = [
    { value: 'LOW', label: 'Low' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'HIGH', label: 'High' },
    { value: 'CRITICAL', label: 'Critical' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Settings className="w-8 h-8" />
            Settings
          </h1>
          <p className="text-muted-foreground">
            Manage your system configuration and preferences
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleResetToDefaults}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Tabs and Content */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Tab Navigation */}
        <Card className="lg:w-64 shrink-0">
          <CardContent className="p-2">
            <nav className="flex flex-row lg:flex-col gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg text-left w-full transition-colors',
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  )}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </nav>
          </CardContent>
        </Card>

        {/* Tab Content */}
        <div className="flex-1">
          {/* General Settings Tab */}
          {activeTab === 'general' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  General Settings
                </CardTitle>
                <CardDescription>
                  Configure your system name, logo, and regional preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Regional Settings Sync Notice */}
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Regional Settings Sync
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        Timezone, Date Format, and Currency settings are synchronized with your Organization settings.
                        Changes made here will also update your Organization profile.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="systemName">System Name</Label>
                  <Input
                    id="systemName"
                    placeholder="IT Asset Management"
                    value={generalSettings.systemName}
                    onChange={(e) =>
                      setGeneralSettings({ ...generalSettings, systemName: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Organization Logo</Label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted">
                      {generalSettings.logoUrl ? (
                        <img
                          src={generalSettings.logoUrl}
                          alt="Logo"
                          className="w-full h-full object-contain rounded-lg"
                        />
                      ) : (
                        <Building2 className="w-8 h-8 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <label htmlFor="logo-upload">
                        <Button variant="outline" asChild>
                          <span>
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Logo
                          </span>
                        </Button>
                      </label>
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoUpload}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Recommended: 200x200px, PNG or SVG
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="dateFormat" className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Date Format
                    </Label>
                    <select
                      id="dateFormat"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={generalSettings.dateFormat}
                      onChange={(e) =>
                        setGeneralSettings({ ...generalSettings, dateFormat: e.target.value })
                      }
                    >
                      {dateFormats.map((format) => (
                        <option key={format.value} value={format.value}>
                          {format.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timeFormat" className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Time Format
                    </Label>
                    <select
                      id="timeFormat"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={generalSettings.timeFormat}
                      onChange={(e) =>
                        setGeneralSettings({ ...generalSettings, timeFormat: e.target.value })
                      }
                    >
                      {timeFormats.map((format) => (
                        <option key={format.value} value={format.value}>
                          {format.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="timezone" className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Timezone
                    </Label>
                    <select
                      id="timezone"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={generalSettings.timezone}
                      onChange={(e) =>
                        setGeneralSettings({ ...generalSettings, timezone: e.target.value })
                      }
                    >
                      {timezones.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <select
                      id="currency"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={generalSettings.currency}
                      onChange={(e) =>
                        setGeneralSettings({ ...generalSettings, currency: e.target.value })
                      }
                    >
                      {currencies.map((currency) => (
                        <option key={currency.value} value={currency.value}>
                          {currency.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Preview Login Page Button */}
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Preview Login Page</p>
                      <p className="text-sm text-muted-foreground">
                        See how your branding appears on the login page
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={() => window.open('/login', '_blank')}
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Preview Login
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Asset Settings Tab */}
          {activeTab === 'assets' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Asset Settings
                </CardTitle>
                <CardDescription>
                  Configure asset tagging, serial numbers, and depreciation tracking
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">Auto-generate Asset Tags</p>
                    <p className="text-sm text-muted-foreground">
                      Automatically generate unique asset tags for new assets
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={assetSettings.autoGenerateAssetTags}
                      onChange={(e) =>
                        setAssetSettings({
                          ...assetSettings,
                          autoGenerateAssetTags: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="assetTagPrefix">Asset Tag Prefix</Label>
                    <Input
                      id="assetTagPrefix"
                      placeholder="AST"
                      value={assetSettings.assetTagPrefix}
                      onChange={(e) =>
                        setAssetSettings({ ...assetSettings, assetTagPrefix: e.target.value.toUpperCase() })
                      }
                      disabled={!assetSettings.autoGenerateAssetTags}
                    />
                    <p className="text-xs text-muted-foreground">
                      Example: AST-000001
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="assetTagLength">Asset Tag Length</Label>
                    <Input
                      id="assetTagLength"
                      type="number"
                      min={4}
                      max={10}
                      placeholder="6"
                      value={assetSettings.assetTagLength}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 4 && val <= 10) {
                          setAssetSettings({ ...assetSettings, assetTagLength: val });
                        }
                      }}
                      disabled={!assetSettings.autoGenerateAssetTags}
                    />
                    <p className="text-xs text-muted-foreground">
                      Number of digits in the sequence (4-10)
                    </p>
                  </div>
                </div>

                <hr className="border-border" />

                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">Require Serial Number</p>
                    <p className="text-sm text-muted-foreground">
                      Make serial number a required field when adding assets
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={assetSettings.requireSerialNumber}
                      onChange={(e) =>
                        setAssetSettings({
                          ...assetSettings,
                          requireSerialNumber: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                <hr className="border-border" />

                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">Enable Depreciation Tracking</p>
                    <p className="text-sm text-muted-foreground">
                      Track asset depreciation over time for accounting purposes
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={assetSettings.enableDepreciationTracking}
                      onChange={(e) =>
                        setAssetSettings({
                          ...assetSettings,
                          enableDepreciationTracking: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="depreciationMethod">Default Depreciation Method</Label>
                  <select
                    id="depreciationMethod"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={assetSettings.defaultDepreciationMethod}
                    onChange={(e) =>
                      setAssetSettings({ ...assetSettings, defaultDepreciationMethod: e.target.value })
                    }
                    disabled={!assetSettings.enableDepreciationTracking}
                  >
                    {depreciationMethods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notification Settings Tab */}
          {activeTab === 'notifications' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Notification Settings
                </CardTitle>
                <CardDescription>
                  Configure email notifications and alerts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">Enable Email Notifications</p>
                    <p className="text-sm text-muted-foreground">
                      Enable or disable all email notifications
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationSettings.enableEmailNotifications}
                      onChange={(e) =>
                        setNotificationSettings({
                          ...notificationSettings,
                          enableEmailNotifications: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="itNotificationEmail">IT Department Notification Email</Label>
                  <Input
                    id="itNotificationEmail"
                    type="email"
                    placeholder="it@ug.sanlamallianz.com"
                    value={notificationSettings.itNotificationEmail}
                    onChange={(e) =>
                      setNotificationSettings({
                        ...notificationSettings,
                        itNotificationEmail: e.target.value,
                      })
                    }
                    disabled={!notificationSettings.enableEmailNotifications}
                  />
                  <p className="text-xs text-muted-foreground">
                    Email address to receive all ticket notifications (new tickets, updates, comments)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="warrantyAlertDays">Warranty Expiry Alert Days</Label>
                  <Input
                    id="warrantyAlertDays"
                    type="number"
                    min={1}
                    max={365}
                    placeholder="30"
                    value={notificationSettings.warrantyExpiryAlertDays}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 1 && val <= 365) {
                        setNotificationSettings({
                          ...notificationSettings,
                          warrantyExpiryAlertDays: val,
                        });
                      }
                    }}
                    disabled={!notificationSettings.enableEmailNotifications}
                  />
                  <p className="text-xs text-muted-foreground">
                    Days before warranty expiry to send alert
                  </p>
                </div>

                <hr className="border-border" />

                <div className="space-y-4">
                  <h3 className="font-medium">Alert Types</h3>
                  {[
                    { key: 'maintenanceReminders', label: 'Maintenance Reminders', desc: 'Get notified when maintenance is due' },
                    { key: 'ticketUpdateNotifications', label: 'Ticket Update Notifications', desc: 'Get notified when tickets are updated' },
                    { key: 'lowStockAlerts', label: 'Low Stock Alerts', desc: 'Get notified when consumable stock is low' },
                    { key: 'documentExpiryAlerts', label: 'Document Expiry Alerts', desc: 'Get notified when documents are about to expire' },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between py-2">
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationSettings[item.key as keyof NotificationSettings] as boolean}
                          onChange={(e) =>
                            setNotificationSettings({
                              ...notificationSettings,
                              [item.key]: e.target.checked,
                            })
                          }
                          className="sr-only peer"
                          disabled={!notificationSettings.enableEmailNotifications}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-disabled:opacity-50"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ticket Settings Tab */}
          {activeTab === 'tickets' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ticket className="w-5 h-5" />
                  Ticket Settings
                </CardTitle>
                <CardDescription>
                  Configure ticketing system behavior and defaults
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">Auto-assign Tickets</p>
                    <p className="text-sm text-muted-foreground">
                      Automatically assign tickets to available technicians
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={ticketSettings.autoAssignTickets}
                      onChange={(e) =>
                        setTicketSettings({
                          ...ticketSettings,
                          autoAssignTickets: e.target.checked,
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="defaultPriority">Default Priority</Label>
                  <select
                    id="defaultPriority"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={ticketSettings.defaultPriority}
                    onChange={(e) =>
                      setTicketSettings({ ...ticketSettings, defaultPriority: e.target.value })
                    }
                  >
                    {priorities.map((priority) => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>

                <hr className="border-border" />

                <div className="space-y-4">
                  {[
                    { key: 'requireCategory', label: 'Require Category', desc: 'Make category a required field when creating tickets' },
                    { key: 'allowPublicTicketSubmission', label: 'Allow Public Ticket Submission', desc: 'Allow users to submit tickets without logging in' },
                    { key: 'slaBreachNotifications', label: 'SLA Breach Notifications', desc: 'Get notified when tickets breach SLA deadlines' },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between py-2 p-4 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={ticketSettings[item.key as keyof TicketSettings] as boolean}
                          onChange={(e) =>
                            setTicketSettings({
                              ...ticketSettings,
                              [item.key]: e.target.checked,
                            })
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
